import React, { useEffect } from 'react';

function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy | UN-BORDERS Client Application';

    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }

    descriptionMeta.setAttribute(
      'content',
      'Privacy Policy for UN-BORDERS Client Application and related client services.'
    );
  }, []);

  const pageStyle = {
    padding: '120px 20px 70px',
    maxWidth: '1120px',
    margin: '0 auto',
    color: '#ffffff',
    textAlign: 'left',
  };

  const panelStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: '18px',
    padding: '34px',
    boxShadow: '0 18px 45px rgba(0, 0, 0, 0.35)',
  };

  const sectionStyle = {
    marginBottom: '32px',
    lineHeight: '1.85',
    fontSize: '16px',
    color: '#ffffff',
  };

  const titleStyle = {
    marginTop: '28px',
    marginBottom: '12px',
    color: '#ffffff',
  };

  const mutedStyle = {
    color: '#e6e6e6',
  };

  const linkStyle = {
    color: '#ffffff',
    fontWeight: '700',
    textDecoration: 'underline',
  };

  return (
    <main style={pageStyle}>
      <div style={panelStyle}>
        <h1>Privacy Policy</h1>
        <p style={mutedStyle}>Last updated: 20 August 2026</p>

        <section style={sectionStyle}>
          <p>
            UN-BORDERS Engineering Consultancy respects the privacy of its clients and authorized users.
            This Privacy Policy explains how the UN-BORDERS Client Application, website, and related
            client services handle information when clients use our systems to view projects, receive
            updates, communicate with UN-BORDERS, and upload project-related files.
          </p>
          <p>
            The client application is intended for authorized business and project use only. It is not a
            public social platform, advertising platform, or data trading service.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>1. Who we are</h2>
          <p>
            The service is operated by <strong>UN-BORDERS Engineering Consultancy</strong>.
          </p>
          <p>
            Website: <strong>https://un-borders.com</strong>
            <br />
            Privacy contact: <a href="mailto:admin@un-borders.com" style={linkStyle}>admin@un-borders.com</a>
            <br />
            Address: <strong>Tower 74, El Nozha Street, Floor 12, Unit 1226, Masr El Gedida, Heliopolis, Cairo, Egypt</strong>
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>2. Scope of this policy</h2>
          <p>
            This policy applies to the UN-BORDERS website, client application, client portal, and related
            client communication and project services provided by UN-BORDERS.
          </p>
          <p>
            It covers information processed when an authorized client user logs in, views project
            information, sends or receives messages, receives project updates, or uploads files related to
            a project handled by UN-BORDERS.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>3. Information we may process</h2>
          <p>
            Depending on the features used by the client, we may process the following limited categories
            of information:
          </p>
          <ul>
            <li>Client account information, such as name, email address, phone number, company or office name, and user role.</li>
            <li>Login and access information needed to authenticate the user and control permissions.</li>
            <li>Project information visible to the client, such as project name, project code, project status, notes, updates, and related project records.</li>
            <li>Messages and communication between the client and UN-BORDERS through the approved communication channels.</li>
            <li>Files uploaded by the client for project purposes, such as drawings, images, documents, comments, and project-related attachments.</li>
            <li>Files or documents provided by UN-BORDERS to the client, such as project deliverables, reports, notices, or related documents.</li>
            <li>Notification data required to send project updates, alerts, or service messages.</li>
            <li>Technical and security data, such as device type, browser or app version, IP address, approximate location derived from the IP address such as country, region, or city, login time, last activity time, activity logs, error logs, and security audit records.</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>4. Passwords and authentication</h2>
          <p>
            UN-BORDERS does not store user passwords in plain text and does not keep passwords in a readable
            form inside the project database. Authentication is handled through a secure login system.
          </p>
          <p>
            UN-BORDERS staff cannot view a user's password. Password reset or recovery, where available,
            is handled through the authentication process and not by exposing the existing password.
          </p>
          <p>
            Users are responsible for keeping their login credentials confidential and must notify
            UN-BORDERS if they believe their account has been accessed without authorization.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>5. Payment, banking, and financial data</h2>
          <p>
            The UN-BORDERS Client Application does not collect payment card numbers, bank account numbers,
            online banking passwords, or card security codes from clients.
          </p>
          <p>
            If the application displays invoices, payment notices, balances, or financial status information,
            this information is provided by UN-BORDERS for viewing, notification, or project administration
            purposes. It is not collected from the client as payment card or banking data.
          </p>
          <p>
            If a client voluntarily sends or uploads a document related to payment confirmation, accounting,
            or project administration, UN-BORDERS will use that document only for the relevant business,
            support, or record-keeping purpose.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>6. How we use information</h2>
          <p>We use information only for legitimate service and business purposes, including to:</p>
          <ul>
            <li>Create and manage authorized client access.</li>
            <li>Show the client their project information and project updates.</li>
            <li>Enable communication between the client and UN-BORDERS.</li>
            <li>Receive, store, and manage project-related files uploaded by the client.</li>
            <li>Send notifications, alerts, and service messages related to projects or accounts.</li>
            <li>Provide support, troubleshooting, and service improvement.</li>
            <li>Understand the approximate region of authorized client access for security, support, troubleshooting, service reliability, and service improvement.</li>
            <li>Protect the security, integrity, and reliability of the application.</li>
            <li>Maintain business, operational, legal, and audit records where required.</li>
          </ul>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>7. Third-party data sharing</h2>
          <p>
            UN-BORDERS does not sell, rent, trade, or share client personal data with third parties for
            advertising, marketing, profiling, data brokerage, or commercial resale.
          </p>
          <p>
            We do not collect client data on behalf of advertisers or unrelated third parties.
          </p>
          <p>
            Limited data may be processed by trusted technical service providers only where necessary to
            operate the service, such as hosting, database, cloud storage, authentication, security,
            backup, email delivery, technical support, or app distribution. These providers are used only
            to help deliver and protect the service.
          </p>
          <p>
            Approximate IP-based location may be processed by trusted technical service providers only
            where necessary for service operation, security, technical support, troubleshooting, or service
            improvement. We do not use this data for advertising, marketing, profiling, data brokerage, or
            commercial resale.
          </p>
          <p>
            Information may also be disclosed where required by applicable law, court order, government
            request, legal process, or to protect the rights, safety, and security of UN-BORDERS, its
            clients, or its systems.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>8. Access control inside UN-BORDERS</h2>
          <p>
            Client information is accessed only by authorized UN-BORDERS personnel who need it to provide
            the service, manage projects, respond to client requests, provide support, or maintain the
            system.
          </p>
          <p>
            Access is controlled according to user roles and permissions. Users should only see the
            information that is relevant to their authorized role and assigned work.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>9. Device permissions</h2>
          <p>
            The application may request device permissions only when needed for a feature selected by the
            user, such as uploading a file, selecting an image, taking a photo, recording or sending an
            attachment, receiving notifications, or using a similar project communication feature.
          </p>
          <p>
            These permissions are not used for advertising, unrelated tracking, or selling user data.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>10. Data storage and security</h2>
          <p>
            We use technical and organizational measures designed to protect information from unauthorized
            access, misuse, loss, alteration, or disclosure. These measures may include authentication,
            role-based access control, secured hosting, restricted access, audit logs, backups, and
            encrypted communication where supported.
          </p>
          <p>
            No digital system can be guaranteed to be completely secure. However, UN-BORDERS takes
            reasonable steps to protect client information and limit access to authorized use only.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>11. Data retention</h2>
          <p>
            We retain information for as long as necessary to provide the service, manage projects,
            maintain business records, resolve support requests, comply with legal or accounting
            obligations, and protect the security and integrity of our systems.
          </p>
          <p>
            When information is no longer required, it may be deleted, archived, anonymized, or securely
            retained according to operational, legal, and contractual requirements.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>12. Client choices and requests</h2>
          <p>
            Subject to applicable law and business requirements, authorized users may contact UN-BORDERS
            to request access to their personal information, correction of inaccurate account information,
            clarification about how their information is used, or deletion of information where deletion
            is legally and operationally possible.
          </p>
          <p>
            Requests can be sent to <a href="mailto:admin@un-borders.com" style={linkStyle}>admin@un-borders.com</a>.
            We may need to verify the identity and authority of the requester before processing a request.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>13. Cookies and website logs</h2>
          <p>
            Our website may use essential cookies and server logs needed for operation, security,
            performance, and troubleshooting. We do not use website cookies to sell personal information.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>14. Children</h2>
          <p>
            The UN-BORDERS Client Application is intended for professional and business use. It is not
            directed to children, and we do not knowingly collect children's personal information through
            the client application.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>15. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy when needed. Any updated version will be published on this
            page with a revised “Last updated” date.
          </p>
        </section>

        <section style={sectionStyle}>
          <h2 style={titleStyle}>16. Contact us</h2>
          <p>
            For privacy questions, concerns, or requests, please contact:
            <br />
            <strong>UN-BORDERS Engineering Consultancy</strong>
            <br />
            Email: <a href="mailto:admin@un-borders.com" style={linkStyle}>admin@un-borders.com</a>
            <br />
            Website: <strong>https://un-borders.com</strong>
          </p>
        </section>

        <hr style={{ margin: '52px 0', borderColor: 'rgba(255,255,255,0.22)' }} />

        <section dir="rtl" style={{ ...sectionStyle, textAlign: 'right' }}>
          <h1>سياسة الخصوصية</h1>
          <p style={mutedStyle}>آخر تحديث: 20 أغسطس 2026</p>

          <p>
            تحترم شركة <strong>UN-BORDERS للاستشارات الهندسية</strong> خصوصية عملائها والمستخدمين
            المصرح لهم. توضح هذه السياسة كيفية تعامل تطبيق العميل والموقع والخدمات المرتبطة مع
            البيانات عند استخدام العميل للنظام لعرض المشروعات، استقبال التحديثات، التواصل مع
            UN-BORDERS، أو رفع ملفات تخص المشروع.
          </p>

          <h2 style={titleStyle}>1. نطاق السياسة</h2>
          <p>
            تنطبق هذه السياسة على موقع UN-BORDERS، وتطبيق العميل، وبوابة العميل، وخدمات التواصل
            وإدارة المشروعات الخاصة بالعملاء.
          </p>

          <h2 style={titleStyle}>2. البيانات التي قد نتعامل معها</h2>
          <ul>
            <li>بيانات حساب العميل مثل الاسم، البريد الإلكتروني، رقم الهاتف، اسم المكتب أو الشركة، ودور المستخدم.</li>
            <li>بيانات الدخول والصلاحيات اللازمة لتوثيق المستخدم والتحكم في الوصول.</li>
            <li>بيانات المشروع الظاهرة للعميل مثل اسم المشروع، الكود، الحالة، الملاحظات، والتحديثات.</li>
            <li>الرسائل والتواصل بين العميل وUN-BORDERS من خلال القنوات المعتمدة.</li>
            <li>الملفات التي يرفعها العميل لأغراض المشروع مثل الرسومات، الصور، المستندات، والمرفقات.</li>
            <li>الملفات أو المستندات التي توفرها UN-BORDERS للعميل مثل المخرجات، التقارير، أو الإشعارات.</li>
            <li>بيانات الإشعارات اللازمة لإرسال تحديثات المشروع أو رسائل الخدمة.</li>
            <li>بيانات فنية وأمنية أساسية مثل نوع الجهاز، إصدار التطبيق أو المتصفح، عنوان IP، الموقع التقريبي المستنتج من عنوان IP مثل الدولة أو المنطقة أو المدينة، وقت الدخول، وقت آخر نشاط، سجلات النشاط، سجلات الأخطاء، وسجلات الأمان.</li>
          </ul>

          <h2 style={titleStyle}>3. كلمات المرور وتسجيل الدخول</h2>
          <p>
            لا تحفظ UN-BORDERS كلمات مرور المستخدمين كنص واضح، ولا يتم تخزين كلمات المرور داخل
            قاعدة بيانات المشروع بشكل يمكن قراءته. تتم عملية تسجيل الدخول من خلال نظام مصادقة آمن.
          </p>
          <p>
            لا يستطيع فريق UN-BORDERS الاطلاع على كلمة مرور المستخدم. وعند الحاجة لاستعادة كلمة
            المرور أو تغييرها، يتم ذلك من خلال آلية المصادقة وليس من خلال كشف كلمة المرور الحالية.
          </p>

          <h2 style={titleStyle}>4. بيانات الدفع والبنوك</h2>
          <p>
            لا يقوم تطبيق العميل بجمع أرقام بطاقات الدفع، أو أرقام الحسابات البنكية، أو كلمات مرور
            الخدمات البنكية، أو أكواد أمان البطاقات من العملاء.
          </p>
          <p>
            إذا ظهرت للعميل فواتير أو إشعارات أو حالة مالية داخل النظام، فهي بيانات مقدمة من
            UN-BORDERS للعرض أو الإخطار أو الإدارة، وليست بيانات بطاقات أو بيانات بنكية يتم جمعها
            من العميل.
          </p>

          <h2 style={titleStyle}>5. استخدام البيانات</h2>
          <p>
            نستخدم البيانات لتشغيل الخدمة، إدارة حسابات العملاء والصلاحيات، عرض بيانات المشروعات،
            تمكين التواصل مع UN-BORDERS، استلام وإدارة ملفات المشروع، إرسال الإشعارات، تقديم الدعم،
            معرفة المنطقة التقريبية لاستخدام حسابات العملاء المصرح لهم لأغراض الأمان والدعم الفني
            وحل المشكلات وموثوقية الخدمة، تحسين الأداء، وحماية النظام.
          </p>

          <h2 style={titleStyle}>6. مشاركة البيانات مع أطراف ثالثة</h2>
          <p>
            لا تقوم UN-BORDERS ببيع أو تأجير أو تداول بيانات العملاء، ولا تشاركها مع أطراف ثالثة
            لأغراض الإعلان أو التسويق أو بناء الملفات الشخصية أو الوساطة في البيانات أو إعادة البيع.
          </p>
          <p>
            قد تتم معالجة بيانات محدودة بواسطة مزودي خدمات تقنيين موثوقين فقط عند الضرورة لتشغيل
            الخدمة، مثل الاستضافة، قاعدة البيانات، التخزين السحابي، المصادقة، الأمان، النسخ الاحتياطي،
            البريد الإلكتروني، الدعم الفني، أو توزيع التطبيق.
          </p>
          <p>
            قد تتم معالجة الموقع التقريبي المستنتج من عنوان IP بواسطة مزودي خدمات تقنيين موثوقين فقط
            عند الضرورة لتشغيل الخدمة أو الأمان أو الدعم الفني أو حل المشكلات أو تحسين الخدمة. لا
            نستخدم هذه البيانات للإعلانات أو التسويق أو بناء ملفات شخصية تجارية أو الوساطة في البيانات
            أو إعادة البيع.
          </p>

          <h2 style={titleStyle}>7. التحكم في الوصول داخل UN-BORDERS</h2>
          <p>
            لا يتم الوصول إلى بيانات العميل إلا بواسطة الأشخاص المصرح لهم داخل UN-BORDERS عند الحاجة
            لتقديم الخدمة أو إدارة المشروع أو الرد على طلب العميل أو صيانة النظام، وذلك وفقًا للصلاحيات
            المحددة لكل مستخدم.
          </p>

          <h2 style={titleStyle}>8. أذونات الجهاز</h2>
          <p>
            قد يطلب التطبيق بعض أذونات الجهاز فقط عند الحاجة لميزة يختارها المستخدم، مثل رفع ملف،
            اختيار صورة، التقاط صورة، إرسال مرفق، استقبال إشعارات، أو استخدام ميزة تواصل خاصة بالمشروع.
            لا تستخدم هذه الأذونات للإعلانات أو التتبع غير المرتبط بالخدمة أو بيع البيانات.
          </p>

          <h2 style={titleStyle}>9. حماية البيانات</h2>
          <p>
            نستخدم إجراءات فنية وتنظيمية لحماية البيانات، مثل المصادقة، التحكم في الصلاحيات، تقييد
            الوصول، سجلات المراجعة، النسخ الاحتياطي، وتأمين الاتصال حيثما كان ذلك مدعومًا.
            ومع ذلك، لا يمكن ضمان أمان أي نظام رقمي بنسبة مطلقة.
          </p>

          <h2 style={titleStyle}>10. الاحتفاظ بالبيانات</h2>
          <p>
            نحتفظ بالبيانات طالما كان ذلك ضروريًا لتقديم الخدمة، إدارة المشروعات، الاحتفاظ بالسجلات
            التجارية، حل طلبات الدعم، الامتثال للالتزامات القانونية أو المحاسبية، وحماية أمان النظام.
          </p>

          <h2 style={titleStyle}>11. حقوق وطلبات العميل</h2>
          <p>
            وفقًا للقوانين والمتطلبات المعمول بها، يمكن للمستخدم المصرح له التواصل مع UN-BORDERS
            لطلب الوصول إلى بياناته الشخصية، تصحيح بيانات الحساب غير الدقيقة، الاستفسار عن استخدام
            البيانات، أو طلب حذف البيانات عندما يكون الحذف ممكنًا قانونيًا وتشغيليًا.
          </p>

          <h2 style={titleStyle}>12. التواصل معنا</h2>
          <p>
            لأي أسئلة أو طلبات متعلقة بالخصوصية، يمكن التواصل مع:
            <br />
            <strong>UN-BORDERS للاستشارات الهندسية</strong>
            <br />
            البريد الإلكتروني: <a href="mailto:admin@un-borders.com" style={linkStyle}>admin@un-borders.com</a>
            <br />
            الموقع: <strong>https://un-borders.com</strong>
          </p>
        </section>
      </div>
    </main>
  );
}

export default PrivacyPolicy;